import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, X, Check, Sparkles } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import type { PhotoAnalysis } from '../types';

interface PhotoCaptureProps {
  photos: PhotoAnalysis[];
  onPhotosChange: (photos: PhotoAnalysis[]) => void;
  maxPhotos?: number;
}

export function PhotoCapture({
  photos,
  onPhotosChange,
  maxPhotos = 8,
}: PhotoCaptureProps): JSX.Element {
  const [photosState, setPhotosState] = useState<PhotoAnalysis[]>(photos);
  const analyzingIdsRef = useState<Set<string>>(new Set())[0];
  
  // Sync with parent
  useEffect(() => {
    setPhotosState(photos);
  }, [photos]);

  const analyzePhoto = useCallback(async (photo: PhotoAnalysis): Promise<PhotoAnalysis> => {
    analyzingIdsRef.add(photo.id);
    setPhotosState(prev => prev.map(p => p.id === photo.id ? { ...p, isAnalyzing: true } : p));
    
    // Simulate AI analysis
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    const analysis: PhotoAnalysis['analysis'] = {
      quality: Math.random() > 0.3 ? 'good' : 'fair',
      lighting: Math.floor(Math.random() * 30) + 70,
      clarity: Math.floor(Math.random() * 20) + 80,
      coverage: Math.floor(Math.random() * 25) + 75,
      suggestions: ['Good lighting', 'Clear focus', 'Add more angles'],
      dominantColors: ['#3b6fff', '#10b981'],
    };
    
    analyzingIdsRef.delete(photo.id);
    const updatedPhoto = { ...photo, analysis, isAnalyzing: false };
    setPhotosState(prev => {
      const updated = prev.map(p => p.id === photo.id ? updatedPhoto : p);
      onPhotosChange(updated);
      return updated;
    });
    return updatedPhoto;
  }, [onPhotosChange]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const remainingSlots = maxPhotos - photosState.length;
      const filesToProcess = acceptedFiles.slice(0, remainingSlots);

      const newPhotos: PhotoAnalysis[] = filesToProcess.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        url: URL.createObjectURL(file),
        file,
        analysis: {
          quality: 'fair',
          lighting: 50,
          clarity: 50,
          coverage: 50,
          suggestions: [],
          dominantColors: [],
        },
        isAnalyzing: true,
      }));

      const updatedPhotos = [...photosState, ...newPhotos];
      setPhotosState(updatedPhotos);
      onPhotosChange(updatedPhotos);

      // Analyze each photo
      for (const photo of newPhotos) {
        await analyzePhoto(photo);
      }
    },
    [photosState, maxPhotos, onPhotosChange, analyzePhoto]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    disabled: photosState.length >= maxPhotos,
  });

  const removePhoto = (id: string) => {
    const updated = photosState.filter((p) => p.id !== id);
    setPhotosState(updated);
    onPhotosChange(updated);
  };

  return (
    <div className="space-y-4">
      {/* Photo grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <AnimatePresence mode="popLayout">
          {photosState.map((photo, index) => (
            <motion.div
              key={photo.id}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="relative aspect-square rounded-xl overflow-hidden group"
            >
              <img
                src={photo.url}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Main photo badge */}
              {index === 0 && (
                <div className="absolute top-2 left-2 bg-primary-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                  Main
                </div>
              )}
              
              {/* Remove button */}
              <button
                onClick={() => removePhoto(photo.id)}
                className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              >
                <X className="w-3 h-3 text-dark-700" />
              </button>
              
              {/* Analysis overlay */}
              {photo.isAnalyzing ? (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles className="w-6 h-6 text-white" />
                  </motion.div>
                </div>
              ) : (
                photo.analysis.quality !== 'poor' && (
                  <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${
                      photo.analysis.quality === 'good'
                        ? 'bg-success text-white'
                        : 'bg-warning text-warning-dark'
                    }`}>
                      <Check className="w-3 h-3" />
                      {photo.analysis.quality === 'good' ? 'Great' : 'Good'}
                    </div>
                  </div>
                )
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Add photo button */}
        {photosState.length < maxPhotos && (
          <div
            {...getRootProps()}
            className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-dark-200 hover:border-primary-300 hover:bg-primary-50/50'
            }`}
          >
            <input {...getInputProps()} />
            <div className="p-2 bg-primary-100 rounded-full">
              <Camera className="w-5 h-5 text-primary-600" />
            </div>
            <span className="text-[10px] text-dark-500 text-center px-2">
              {photosState.length === 0 ? 'Add Photos' : `${photosState.length}/${maxPhotos}`}
            </span>
          </div>
        )}
      </div>

      {/* Photo tips */}
      {photosState.length > 0 && photosState.some((p) => !p.isAnalyzing) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary-50 rounded-lg p-3 flex items-start gap-2"
        >
          <Sparkles className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-primary-700">AI Photo Tips</p>
            <ul className="text-primary-600 text-xs mt-1 space-y-1">
              <li>• Use natural lighting for better results</li>
              <li>• Show multiple angles of your item</li>
              <li>• Keep the background clean and uncluttered</li>
            </ul>
          </div>
        </motion.div>
      )}
    </div>
  );
}
